import { Button } from "@/components/ui/button";
import { AlertTriangle, FileText, Upload, X } from "lucide-react";
import { DragEvent, useRef, useState } from "react";

interface UploadedFile {
  file: File;
  name: string;
  id: string;
}

interface ChamadoFileUploadProps {
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
}

const MAX_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "application/pdf"];

export default function ChamadoFileUpload({
  onFilesChange,
  maxFiles = 5,
}: ChamadoFileUploadProps) {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const validate = (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return "Apenas PNG, JPEG ou PDF são permitidos.";
    }
    if (file.size > MAX_SIZE) {
      return "Tamanho máximo: 5 MB.";
    }
    return null;
  };

  const handleAdd = (fileList: FileList | null) => {
    if (!fileList) return;

    const newFiles = Array.from(fileList);

    // Verificar se excede o limite
    if (files.length + newFiles.length > maxFiles) {
      setError(`Máximo de ${maxFiles} arquivos permitidos.`);
      return;
    }

    // Validar cada arquivo
    for (const file of newFiles) {
      const err = validate(file);
      if (err) {
        setError(`${file.name}: ${err}`);
        return;
      }
    }

    setError(null);

    // Adicionar arquivos
    const uploadedFiles = newFiles.map((file) => ({
      file,
      name: file.name,
      id: Math.random().toString(36).substring(7),
    }));

    const updatedFiles = [...files, ...uploadedFiles];
    setFiles(updatedFiles);

    // Notificar o componente pai
    onFilesChange(updatedFiles.map((f) => f.file));
  };

  const removeFile = (id: string) => {
    const updatedFiles = files.filter((f) => f.id !== id);
    setFiles(updatedFiles);
    onFilesChange(updatedFiles.map((f) => f.file));
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    handleAdd(e.dataTransfer.files);
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  return (
    <div className="space-y-4">
      <p className="font-semibold text-n-text-neutral">Anexar documentos:</p>
      <p className="text-sm text-n-text-neutral">
        PNG, JPEG ou PDF (máx. 5 MB) - até {maxFiles} arquivos
      </p>

      <div
        onDragOver={onDragOver}
        onDrop={onDrop}
        className="w-full h-40 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-n-green transition-colors"
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="w-6 h-6 text-gray-400 mb-2" />
        <span className="text-sm text-n-text-neutral">
          Arraste e solte ou clique para selecionar
        </span>
        <span className="text-xs text-gray-500 mt-1">
          {files.length}/{maxFiles} arquivos
        </span>
        <input
          type="file"
          accept=".png,.jpeg,.jpg,.pdf"
          multiple
          ref={inputRef}
          className="hidden"
          onChange={(e) => handleAdd(e.target.files)}
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      {files.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">
            Arquivos selecionados:
          </h4>
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border"
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-n-text-neutral" />
                <span className="text-sm font-medium">{file.name}</span>
                <span className="text-xs text-gray-500">
                  ({(file.file.size / 1024 / 1024).toFixed(2)} MB)
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeFile(file.id)}
                className="text-red-600 hover:text-red-800 hover:bg-red-50"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
